import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { 
  Search, Eye, Plus, Calendar, Truck, User, MapPin,
  ChevronLeft, ChevronRight, Filter, RefreshCw,
  CheckCircle, Clock, Play, XCircle, BarChart3
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../../hooks/use-toast';
import { cn } from '../../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration des statuts
const RUN_STATUS_CONFIG = {
  planned: { label: 'Planifiée', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Clock },
  in_progress: { label: 'En cours', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Play },
  completed: { label: 'Terminée', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  cancelled: { label: 'Annulée', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle }
};

const RunStatusBadge = ({ status }) => {
  const config = RUN_STATUS_CONFIG[status] || RUN_STATUS_CONFIG.planned;
  const Icon = config.icon;
  
  return (
    <Badge className={cn('flex items-center gap-1.5 px-2.5 py-1 border', config.color)}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </Badge>
  );
};

const ContractRunsList = () => {
  const navigate = useNavigate();
  const { contractId } = useParams();
  
  const [runs, setRuns] = useState([]);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 15;
  
  // KPI
  const [kpi, setKpi] = useState({
    total: 0,
    planned: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0
  });

  const fetchContract = useCallback(async () => {
    if (!contractId) return;
    try {
      const response = await axios.get(`${API}/contracts/${contractId}`);
      setContract(response.data);
    } catch (error) {
      console.error('Error fetching contract:', error);
    }
  }, [contractId]);

  const fetchRuns = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append('skip', (currentPage - 1) * itemsPerPage);
      params.append('limit', itemsPerPage);
      
      if (contractId) {
        params.append('contract_id', contractId);
      }
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (dateFilter) {
        params.append('date_from', dateFilter);
        params.append('date_to', dateFilter);
      }
      
      const response = await axios.get(`${API}/contract-runs?${params.toString()}`);
      
      setRuns(response.data.items || []);
      setTotalItems(response.data.total || 0);
      
      // Calculer les KPI
      const allParams = new URLSearchParams();
      if (contractId) allParams.append('contract_id', contractId);
      allParams.append('limit', '1000');
      
      const allResponse = await axios.get(`${API}/contract-runs?${allParams.toString()}`);
      const allRuns = allResponse.data.items || [];
      
      setKpi({
        total: allRuns.length,
        planned: allRuns.filter(r => r.status === 'planned').length,
        in_progress: allRuns.filter(r => r.status === 'in_progress').length,
        completed: allRuns.filter(r => r.status === 'completed').length,
        cancelled: allRuns.filter(r => r.status === 'cancelled').length
      });
      
    } catch (error) {
      console.error('Error fetching runs:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les opérations",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [contractId, currentPage, statusFilter, dateFilter]);

  useEffect(() => {
    fetchContract();
    fetchRuns();
  }, [fetchContract, fetchRuns]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, dateFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatPrice = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Terminologie du contrat
  const terminology = contract?.terminology || 'operation';
  const terminologyLabels = {
    transfert: { singular: 'Transfert', plural: 'Transferts', isMasculine: true },
    operation: { singular: 'Opération', plural: 'Opérations', isMasculine: false },
    course: { singular: 'Course', plural: 'Courses', isMasculine: false }
  };
  const termLabel = terminologyLabels[terminology] || terminologyLabels.operation;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">
            {contractId && contract ? `${termLabel.plural} - ${contract.contractNumber}` : `Toutes les ${termLabel.plural}`}
          </h1>
          <p className="text-gray-500 mt-1">
            {contract ? contract.clientName : 'Gestion des opérations de contrat'}
          </p>
        </div>
        {contractId && contract?.status === 'active' && (
          <Button 
            onClick={() => navigate(`/admin/contracts/${contractId}/runs/new`)}
            className="bg-red-600 hover:bg-red-700"
            data-testid="create-run-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {termLabel.isMasculine ? 'Nouveau' : 'Nouvelle'} {termLabel.singular}
          </Button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total</p>
                <p className="text-2xl font-bold text-slate-900">{kpi.total}</p>
              </div>
              <BarChart3 className="w-6 h-6 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Planifiées</p>
                <p className="text-2xl font-bold text-blue-900">{kpi.planned}</p>
              </div>
              <Clock className="w-6 h-6 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">En cours</p>
                <p className="text-2xl font-bold text-amber-900">{kpi.in_progress}</p>
              </div>
              <Play className="w-6 h-6 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Terminées</p>
                <p className="text-2xl font-bold text-emerald-900">{kpi.completed}</p>
              </div>
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Annulées</p>
                <p className="text-2xl font-bold text-red-900">{kpi.cancelled}</p>
              </div>
              <XCircle className="w-6 h-6 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full md:w-48"
                data-testid="date-filter"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="planned">Planifiées</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="completed">Terminées</SelectItem>
                <SelectItem value="cancelled">Annulées</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={fetchRuns} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : runs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Calendar className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">{termLabel.isMasculine ? 'Aucun' : 'Aucune'} {termLabel.singular.toLowerCase()} {termLabel.isMasculine ? 'trouvé' : 'trouvée'}</p>
              {contractId && contract?.status === 'active' && (
                <Button 
                  onClick={() => navigate(`/admin/contracts/${contractId}/runs/new`)}
                  className="mt-4 bg-red-600 hover:bg-red-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer {termLabel.isMasculine ? 'un' : 'une'} {termLabel.singular.toLowerCase()}
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">N° {termLabel.singular}</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  {!contractId && <TableHead className="font-semibold">Client</TableHead>}
                  <TableHead className="font-semibold">Véhicule</TableHead>
                  <TableHead className="font-semibold">Chauffeur</TableHead>
                  <TableHead className="font-semibold">Stops</TableHead>
                  <TableHead className="font-semibold">Prix</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow 
                    key={run.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/admin/contract-runs/${run.id}`)}
                    data-testid={`run-row-${run.id}`}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900" title={run.runNumber || ''}>
                          {run.shortReference || run.runNumber || '-'}
                        </span>
                        {run.shortReference && run.runNumber && (
                          <span className="text-xs text-gray-400 truncate max-w-[200px]" title={run.runNumber}>
                            {run.runNumber}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(run.date)}
                      </div>
                    </TableCell>
                    {!contractId && (
                      <TableCell>
                        <span className="text-gray-900">{run.clientName || '-'}</span>
                        <div className="text-xs text-gray-500">{run.contractNumber}</div>
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Truck className="w-4 h-4 text-gray-400" />
                        {run.plannedVehiclePlate || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <User className="w-4 h-4 text-gray-400" />
                        {run.plannedDriverName || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className={cn(
                          "font-medium",
                          run.stopsCompletedCount === run.stopsCount ? "text-emerald-600" : "text-gray-600"
                        )}>
                          {run.stopsCompletedCount || 0}/{run.stopsCount || 0}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">{formatPrice(run.unitPrice)}</span>
                    </TableCell>
                    <TableCell>
                      <RunStatusBadge status={run.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/contract-runs/${run.id}`);
                        }}
                        data-testid={`view-run-${run.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-gray-500">
              Affichage {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} sur {totalItems}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-600">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ContractRunsList;
